import React, { useEffect, useState } from "react";
import axios from "axios";
import "../css/vehicle.css";
import useAuthRedirect from "../hooks/useAuthRedirect";
import AdminNav from "../components/adminNav";
import Time from "../components/time";
import { useParams, Link, useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { hasPermission } from "../utils/permissionHelper";
import Access from "../components/access";

/* ================= helpers ================= */

// robust number parser that tolerates commas, units, spaces, etc.
// returns 0 if missing/invalid
const safeNum = (val) => {
  if (val === null || val === undefined) return 0;
  if (typeof val === "number" && isFinite(val)) return val;
  if (typeof val === "string") {
    // keep digits, optional leading minus, and dot; strip the rest
    const cleaned = val
      .toString()
      .replace(/,/g, "")         // remove thousands separators
      .replace(/[^\d.\-]/g, ""); // remove non-numeric except dot/minus
    const n = parseFloat(cleaned);
    return isNaN(n) ? 0 : n;
  }
  return 0;
};

const formatDMY = (dLike) => {
  const d = new Date(dLike);
  return isNaN(d) ? "" : d.toLocaleDateString("en-GB");
};

const pad2 = (n) => String(n).padStart(2, "0");

// minutes -> "HH:MM"
const minutesToHHMM = (mins) => {
  const m = Math.max(0, Math.floor(mins));
  const hh = Math.floor(m / 60);
  const mm = m % 60;
  return `${pad2(hh)}:${pad2(mm)}`;
};

/* ================= component ================= */

const HireVehicleConRep = () => {
  useAuthRedirect();
  const { id, branch } = useParams();

  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [vehicleBasis, setVehicleBasis] = useState(""); // "Hour" | "Km" | "Ton" | "Month"

  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const startYear = 2007;
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);

  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const canView = hasPermission("Vehicle Diesel Consumption", "view");

  // site / company info for Excel header
  const [site, setSite] = useState({
    ok: true,
    name: "Infraconsoft",
    logo: null,
    address: "",
    email: "",
    phone: "",
    _ts: 0,
  });

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const res = await axios.get("https://rfs2.infraconsoft.net.in/api/siteinfo");
        const s = res?.data || {};
        let logo = s.logo || null;
        if (logo && !/^https?:\/\//i.test(logo)) {
          if (!logo.startsWith("/")) logo = `/${logo}`;
          logo = `${window.location.origin}${logo}`;
        }
        if (!ignore) {
          setSite({
            ok: !!s.ok,
            name: s.name || "Infraconsoft",
            logo,
            address: s.address || "",
            email: s.email || "",
            phone: s.phone || "",
            _ts: Date.now(),
          });
        }
      } catch (e) {
        console.error("Failed to load /siteinfo", e);
      }
    })();
    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, selectedMonth]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        "https://rfs2.infraconsoft.net.in/api/get_hire_veh_report_api",
        {
          params: {
            month: selectedMonth,
            year: selectedYear,
            branch: branch,
            id: id,
          },
        }
      );

      const responseData = response?.data?.postionData || [];
      setData(responseData);
      setFilteredData(responseData);

      if (responseData.length > 0 && responseData[0].fld_vbasis) {
        setVehicleBasis(String(responseData[0].fld_vbasis));
      } else {
        setVehicleBasis("");
      }
    } catch (error) {
      console.error("Error fetching postionData:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    const term = (e.target.value || "").toLowerCase();
    setSearchTerm(term);
    if (!term) {
      setFilteredData(data);
      return;
    }
    const filtered = data.filter(
      (item) =>
        String(item.stn_purnumber || "").toLowerCase().includes(term) ||
        String(item.stn_dpt || "").toLowerCase().includes(term)
    );
    setFilteredData(filtered);
    setCurrentPage(1);
  };

  // Pagination
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentData = filteredData.slice(indexOfFirstItem, indexOfLastItem);

  /* ================= calculations (table rows) =================
     Rules:
     - Km basis (also Ton/Month for lack of better source): working = endkm - stkm; cumulative is running sum on the page; diesel consumption = km / liter
     - Hour basis: working = fld_wohrs (decimal hours); cumulative sum of hours; diesel consumption = hours / liter
     - Diesel Qty and Cum. Diesel Qty always shown
     - All numeric fields coerced with safeNum()
  */

  /* ================= Excel export with site header ================= */

  const exportToExcel = () => {
  // robust numeric parser (tolerates "1,234", "12.5 km", etc.)
  const num = (v) => {
    if (v === null || v === undefined) return 0;
    if (typeof v === "number" && isFinite(v)) return v;
    const cleaned = String(v).replace(/,/g, "").replace(/[^\d.\-]/g, "");
    const n = parseFloat(cleaned);
    return Number.isNaN(n) ? 0 : n;
  };

  // Export ALL filtered rows for the month
  let cumKm = 0;
  let cumHrs = 0;
  let cumTrips = 0;
  let cumDiesel = 0;

  // prefer page-level basis but compute per row safely in case of mixed data
  const pageIsHour = String(vehicleBasis || "").trim() === "Hour";

  const headers = pageIsHour
    ? [
        "S.No",
        "Date",
        "Working Hours",
        "Cumulative Hours",
        "Number Of Trips",
        "Cumulative Trips",
        "Diesel quantity",
        "Cum. Diesel quantity",
        "Diesel Consumption (Hrs/L)",
      ]
    : [
        "S.No",
        "Date",
        "Working KM",
        "Cumulative KM",
        "Number Of Trips",
        "Cumulative Trips",
        "Diesel quantity",
        "Cum. Diesel quantity",
        "Diesel Consumption (Km/L)",
      ];

  const rows = (filteredData || []).map((item, idx) => {
    const basis = String(item?.fld_vbasis || vehicleBasis || "").trim();

    const stkm  = num(item?.fld_stkm);
    const endkm = num(item?.fld_endkm);
    const wkm   = Math.max(0, endkm - stkm);

    // trips: for Month/Ton some data uses fld_noloads
    const trips = (basis === "Month" || basis === "Ton")
      ? num(item?.fld_noloads ?? item?.fld_noload)
      : num(item?.fld_noload);

    const diesel = num(item?.fld_dieselqty);

    cumTrips  += trips;
    cumDiesel += diesel;

    const isHourRow = basis === "Hour"; // row-safe

    if (isHourRow) {
      const hrs = num(item?.fld_wohrs); // decimal hours
      cumHrs += hrs;
      const cons = diesel > 0 ? hrs / diesel : 0; // hours per liter
      return [
        idx + 1,
        formatDMY(item?.fld_vdate),
        hrs.toFixed(2),
        cumHrs.toFixed(2),
        trips,
        cumTrips,
        diesel.toFixed(2),
        cumDiesel.toFixed(2),
        cons.toFixed(2),
      ];
    } else {
      cumKm += wkm;
      const cons = diesel > 0 ? wkm / diesel : 0; // km per liter
      return [
        idx + 1,
        formatDMY(item?.fld_vdate),
        wkm.toFixed(2),
        cumKm.toFixed(2),
        trips,
        cumTrips,
        diesel.toFixed(2),
        cumDiesel.toFixed(2),
        cons.toFixed(2),
      ];
    }
  });

  const COLS = headers.length;
  const fullWidth = (text = "") => [text, ...Array(COLS - 1).fill("")];
  const title = `Hire Vehicle — ${pageIsHour ? "Hours" : "KM"} & Diesel — ${String(
    selectedMonth
  ).padStart(2, "0")}/${selectedYear} — VCode: ${id || ""} — Branch: ${branch || ""}`;

  const aoa = [
    fullWidth((site?.name || "Infraconsoft").toUpperCase()),
    fullWidth(site?.address || ""),
    fullWidth(
      [site?.phone ? `Ph: ${site.phone}` : "", site?.email ? `Email: ${site.email}` : ""]
        .filter(Boolean)
        .join(" | ")
    ),
    [], // spacer
    fullWidth(title),
    [], // spacer
    headers,
    ...rows,
  ];

  const ws = XLSX.utils.aoa_to_sheet(aoa);

  ws["!merges"] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: COLS - 1 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: COLS - 1 } },
    { s: { r: 2, c: 0 }, e: { r: 2, c: COLS - 1 } },
    { s: { r: 4, c: 0 }, e: { r: 4, c: COLS - 1 } },
  ];

  ws["!cols"] = headers.map((h, i) => {
    if (i === 0) return { wch: 6 };  // S.No
    if (i === 1) return { wch: 12 }; // Date
    return { wch: 18 };
  });

  ws["!freeze"] = { xSplit: 0, ySplit: 6, topLeftCell: "A7", activePane: "bottomLeft" };

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Hire Vehicle Diesel");

  const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const blob = new Blob([excelBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
  });

  saveAs(blob, `hire_Vehicle_Diesel_${selectedMonth}-${selectedYear}_${id || ""}.xlsx`);
};


  if (!canView) {
    return (
      <div id="admin-container">
        <AdminNav />
        <div className="content-right" style={{ padding: 20 }}>
          <Access />
        </div>
      </div>
    );
  }

  return (
    <div id="admin-container">
      <AdminNav />
      <div className="content-right">
        <section>
          <div className="back-div">
            <h2 className="add-txt">Hire Vehicle - Diesel Report</h2>
            <Link to={`/vehicle_consumereport_list_hire`}>
              <p className="back-button">
                <i className="fa-solid fa-backward"></i> Back
              </p>
            </Link>
          </div>
          <Time />
        </section>

        <div className="add-new-container">
          <button className="add-new-btn" onClick={exportToExcel}>
            <i className="fa-solid fa-file-excel"></i> Export to Excel
          </button>
        </div>

        {/* Month & Year Selection */}
        <div className="month-year-container">
          <p className="title">Select your Month & Year:</p>
          <div className="select-container">
            <select
              value={selectedMonth}
              onChange={(e) => {
                setSelectedMonth(parseInt(e.target.value, 10));
                setCurrentPage(1);
              }}
              className="custom-select"
            >
              {[...Array(12).keys()].map((i) => (
                <option key={i + 1} value={i + 1}>
                  {(i + 1).toString().padStart(2, "0")}
                </option>
              ))}
            </select>

            <select
              value={selectedYear}
              onChange={(e) => {
                setSelectedYear(parseInt(e.target.value, 10));
                setCurrentPage(1);
              }}
              className="custom-select"
            >
              {[...Array(currentYear - startYear + 1).keys()].map((i) => (
                <option key={startYear + i} value={startYear + i}>
                  {startYear + i}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="content-table">
          <table>
            <thead>
              <tr>
                <th>S.No</th>
                <th>Date</th>
                {vehicleBasis === "Hour" ? (
                  <>
                    <th>Working Hours</th>
                    <th>Cumulative Hours</th>
                  </>
                ) : (
                  <>
                    <th>Working KM</th>
                    <th>Cumulative KM</th>
                  </>
                )}
                <th>Number Of Trips</th>
                <th>Cumulative Trips</th>
                <th>Diesel quantity</th>
                <th>Cum. Diesel quantity</th>
                <th>
                  Diesel Consumption{" "}
                  {vehicleBasis === "Hour" ? "(Hrs/L)" : "(Km/L)"}
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="16" className="loading-text">
                    Loading data, please wait...
                  </td>
                </tr>
              ) : currentData.length > 0 ? (
                (() => {
  // page-scoped running totals for visible rows
  let pageCumKm = 0;
  let pageCumHrs = 0;
  let pageCumTrips = 0;
  let pageCumDiesel = 0;

  return currentData.map((item, index) => {
    const stkm   = safeNum(item.fld_stkm);
    const endkm  = safeNum(item.fld_endkm);
    const wkm    = Math.max(0, endkm - stkm);
    const diesel = safeNum(item.fld_dieselqty);

    // Normalize basis and trips field
    const basis = String(item.fld_vbasis || "").trim();
    const rowTrips =
      basis === "Month" || basis === "Ton"
        ? safeNum(item.fld_noloads ?? item.fld_noload)
        : safeNum(item.fld_noload);

    pageCumTrips  += rowTrips;
    pageCumDiesel += diesel;

    let workingDisplay = "";
    let cumDisplay = "";
    let consumption = 0;

    if (basis === "Hour") {
      // hours are stored as decimal hours
      const hrs = safeNum(item.fld_wohrs);
      pageCumHrs += hrs;
      workingDisplay = hrs.toFixed(2);
      cumDisplay     = pageCumHrs.toFixed(2);
      consumption    = diesel > 0 ? hrs / diesel : 0; // Hrs/L
    } else {
      // Km / Ton / Month -> KM-based display
      pageCumKm += wkm;
      workingDisplay = wkm.toFixed(2);
      cumDisplay     = pageCumKm.toFixed(2);
      consumption    = diesel > 0 ? wkm / diesel : 0; // Km/L
    }

    return (
      <tr key={index}>
        <td>{indexOfFirstItem + index + 1}</td>
        <td>{formatDMY(item.fld_vdate)}</td>

        <td className="fontsbillblue"><div align="center">{workingDisplay}</div></td>
        <td className="fontsbillblue"><div align="center">{cumDisplay}</div></td>

        <td className="fontsbillblue"><div align="center">{rowTrips}</div></td>
        <td className="fontsbillblue"><div align="center">{pageCumTrips}</div></td>
        <td className="fontsbillblue"><div align="center">{safeNum(diesel).toFixed(2)}</div></td>
        <td className="fontsbillblue"><div align="center">{pageCumDiesel.toFixed(2)}</div></td>
        <td className="fontsbillblue"><div align="center">{Number(consumption).toFixed(2)}</div></td>
      </tr>
    );
  });
})()
              ) : (
                <tr>
                  <td colSpan="16">No Data Available</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="pagination">
          <button
            onClick={() => paginate(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </button>

          <button
            onClick={() => paginate(1)}
            className={currentPage === 1 ? "active" : ""}
          >
            1
          </button>

          {filteredData.length > 0 &&
            [
              ...Array(
                Math.min(3, Math.ceil(filteredData.length / itemsPerPage) - 1)
              ),
            ].map((_, index) => {
              const pageNumber = currentPage + index;
              if (
                pageNumber > 1 &&
                pageNumber <= Math.ceil(filteredData.length / itemsPerPage)
              ) {
                return (
                  <button
                    key={pageNumber}
                    onClick={() => paginate(pageNumber)}
                    className={currentPage === pageNumber ? "active" : ""}
                  >
                    {pageNumber}
                  </button>
                );
              }
              return null;
            })}

          <button
            onClick={() => paginate(currentPage + 1)}
            disabled={
              currentPage === Math.ceil(filteredData.length / itemsPerPage)
            }
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default HireVehicleConRep;
